import * as THREE from 'three';
import { cerebrumSurfaceRadius, isInsideCerebrum } from './brainShape';

/**
 * Builds the cerebrum wireframe by projecting an icosphere's vertices onto the
 * canonical cerebrum surface (see brainShape.ts), raycasting each vertex
 * direction against the lobe signed distance field.
 *
 * Detail level 5 (~10k vertices) is deliberate: the anatomical features here are
 * large-scale geometry (the temporal lobe, the Sylvian fissure) plus chunky
 * gyri, and a denser mesh makes the wireframe so busy that the triangulation
 * visually competes with the form instead of revealing it.
 */
export function createBrainGeometry(): THREE.BufferGeometry {
  const icosahedron = new THREE.IcosahedronGeometry(1, 5);
  const positions = icosahedron.attributes.position;
  const vertexCount = positions.count;
  const deformed = new Float32Array(vertexCount * 3);

  for (let i = 0; i < vertexCount; i++) {
    // IcosahedronGeometry of radius 1 already yields unit-length vertices.
    const dx = positions.getX(i);
    const dy = positions.getY(i);
    const dz = positions.getZ(i);

    const r = cerebrumSurfaceRadius(dx, dy, dz);

    deformed[i * 3] = dx * r;
    deformed[i * 3 + 1] = dy * r;
    deformed[i * 3 + 2] = dz * r;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(deformed, 3));
  geometry.setIndex(icosahedron.index);
  geometry.computeVertexNormals();
  icosahedron.dispose();

  return geometry;
}

/** Re-exported so particle systems don't need to know which module owns the shape. */
export { isInsideCerebrum as isInsideBrainVolume };
